/**
 * Tâche 1353 (Eric 2026-06-30) — Résultat d'une reformulation.
 *
 * La définition unifiée est DÉJÀ écrite quand cette fenêtre s'ouvre (choix
 * d'Eric : on nettoie le terme courant tout de suite). Elle sert donc à deux
 * choses : montrer ce qui a changé — avant et après côte à côte, avec le
 * détail mot à mot de `lib/diffMots.js` — et arbitrer les fragments que l'IA
 * juge étrangers au terme. Ces migrations-là ne sont JAMAIS automatiques :
 * déplacer du texte d'un terme vers un autre, ou créer un terme, est une
 * décision de lexicographe.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, MagicWand, CircleNotch, ArrowRight, Plus } from "@phosphor-icons/react";
import { diffMots } from "../../lib/diffMots";
import { API_BASE as API } from "../../lib/apiBase";

const MotsDiff = ({ avant, apres, cote }) => {
  const parts = diffMots(avant, apres);
  return (
    <p className="text-[12px] leading-snug whitespace-pre-wrap break-words">
      {parts.map((p, i) => {
        if (p.type === "egal") return <span key={i} className="text-zinc-600">{p.texte}</span>;
        if (cote === "avant" && p.type === "supprime") {
          return <span key={i} className="text-red-700 line-through">{p.texte}</span>;
        }
        if (cote === "apres" && p.type === "ajoute") {
          return <span key={i} className="text-[#002FA7] font-semibold">{p.texte}</span>;
        }
        return null;
      })}
    </p>
  );
};

export default function ReformulationResultModal({
  open,
  onClose,
  result,
  lang = "fr",
  onApplied,
}) {
  const tr = (fr, en) => (lang === "fr" ? fr : en);
  const [busy, setBusy] = useState(null);
  const [traitees, setTraitees] = useState({});

  if (!open || !result) return null;

  const migrations = (result.migrations || []).filter((m) => !traitees[m.id]);
  const nbSources =
    (result.sources_preserved?.source_video_ids || []).length
    + (result.sources_preserved?.source_text_doc_ids || []).length;

  const agir = async (prop, action) => {
    setBusy(prop.id);
    try {
      const { data } = await axios.post(
        `${API}/api/jargon/migration-proposals/${prop.id}/${action}`,
        {},
        { withCredentials: true },
      );
      setTraitees((t) => ({ ...t, [prop.id]: action }));
      if (action === "apply") {
        toast.success(
          data?.created
            ? tr(`Terme « ${data.target_term} » créé avec ce fragment.`,
                 `Term "${data.target_term}" created with this fragment.`)
            : tr(`Fragment ajouté à « ${data.target_term} ».`,
                 `Fragment added to "${data.target_term}".`),
        );
        if (onApplied) await onApplied();
      } else {
        toast.info(tr("Proposition écartée.", "Proposal dismissed."));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message
        || tr("Erreur", "Error"));
    } finally {
      setBusy(null);
    }
  };

  /** Tâche 1452 — l'une des trois issues, puis on referme : laisser la fenêtre
      ouverte après une décision globale laisserait croire qu'il reste à
      choisir. */
  const decider = async (choix) => {
    setBusy(choix);
    try {
      const { data } = await axios.post(
        `${API}/api/jargon/${result.entry_id}/reformulate/decision`,
        { choix },
        { withCredentials: true },
      );
      if (choix === "refuser") {
        toast.success(tr("Reformulation refusée : la définition d'avant est remise.",
                         "Reformulation refused: the previous definition is restored."));
      } else if (choix === "reduire") {
        toast.success(data.migrations_ecartees
          ? tr(`Définition raccourcie gardée · ${data.migrations_ecartees} proposition(s) écartée(s).`,
               `Shortened definition kept · ${data.migrations_ecartees} proposal(s) dismissed.`)
          : tr("Définition raccourcie gardée.", "Shortened definition kept."));
      } else {
        toast.success(tr(`Définition raccourcie gardée · ${data.migrations_appliquees} fragment(s) déplacé(s).`,
                         `Shortened definition kept · ${data.migrations_appliquees} fragment(s) moved.`));
        // Un échec est DIT avec son motif : un silence laisserait croire que
        // tous les fragments sont arrivés à destination.
        if (data.echecs?.length) {
          toast.warning(tr(`${data.echecs.length} fragment(s) non déplacé(s).`,
                           `${data.echecs.length} fragment(s) not moved.`));
        }
      }
      if (onApplied) await onApplied();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message
        || tr("Erreur", "Error"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="reformulation-result-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[88vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-zinc-200 flex items-start justify-between bg-zinc-50">
          <div className="min-w-0">
            <h3 className="font-bold text-base text-zinc-900 flex items-center gap-2">
              <MagicWand size={18} weight="bold" className="text-violet-600" />
              {tr("Définition reformulée", "Definition reformulated")}
              <span className="text-zinc-500 font-normal truncate">— {result.term}</span>
            </h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              {result.unchanged
                ? tr("La définition était déjà propre : rien n'a été modifié.",
                     "The definition was already clean: nothing was changed.")
                : tr("La nouvelle définition est enregistrée. L'ancienne reste dans l'historique du terme.",
                     "The new definition is saved. The previous one stays in the term's history.")}
            </p>
            <p className="text-[11px] text-emerald-700 mt-0.5" data-testid="reformulation-sources-kept">
              {tr(
                `${nbSources} source(s) cliquable(s) conservée(s) — reformuler le texte ne change pas d'où vient l'information.`,
                `${nbSources} clickable source(s) kept — reformulating the text does not change where the information comes from.`,
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-zinc-200 rounded flex-shrink-0"
            data-testid="close-reformulation-modal-btn"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {result.summary && (
            <p className="text-[12px] text-zinc-700 bg-violet-50 border border-violet-200 rounded px-3 py-2"
               data-testid="reformulation-summary">
              {result.summary}
            </p>
          )}

          <div className="grid md:grid-cols-2 gap-3">
            <div className="rounded border border-zinc-200 bg-zinc-50 px-3 py-2 min-w-0">
              <p className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold mb-1">
                {tr("Avant", "Before")}
              </p>
              <MotsDiff avant={result.definition_before} apres={result.definition_after} cote="avant" />
            </div>
            <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 min-w-0">
              <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-1">
                {tr("Après", "After")}
              </p>
              <MotsDiff avant={result.definition_before} apres={result.definition_after} cote="apres" />
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-zinc-700 uppercase tracking-wider mb-2">
              {tr("Fragments à déplacer", "Fragments to move")}
            </h4>
            {migrations.length === 0 ? (
              <p className="text-[12px] text-zinc-500" data-testid="reformulation-no-migration">
                {tr("Cette définition ne parle que de ce terme : rien à déplacer.",
                    "This definition only covers this term: nothing to move.")}
              </p>
            ) : (
              <ul className="space-y-2">
                {migrations.map((m) => (
                  <li
                    key={m.id}
                    className="rounded border border-amber-200 bg-amber-50 px-3 py-2"
                    data-testid={`migration-proposal-${m.id}`}
                  >
                    <p className="text-[11px] text-zinc-600 flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-zinc-800">{result.term}</span>
                      <ArrowRight size={11} weight="bold" />
                      <span className="font-semibold text-zinc-800">{m.target_term}</span>
                      {!m.target_exists && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white border border-amber-300 text-amber-800"
                          data-testid={`migration-new-term-${m.id}`}
                        >
                          <Plus size={9} weight="bold" />
                          {tr("terme à créer", "term to create")}
                        </span>
                      )}
                    </p>
                    <p className="text-[12px] text-zinc-800 mt-1 whitespace-pre-wrap break-words">{m.text}</p>
                    {m.reason && (
                      <p className="text-[11px] text-zinc-500 mt-0.5 italic">{m.reason}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => agir(m, "apply")}
                        disabled={busy === m.id}
                        className="text-[11px] font-semibold px-3 py-1.5 rounded bg-[#002FA7] text-white hover:bg-[#001F7A] disabled:opacity-50 inline-flex items-center gap-1.5"
                        data-testid={`migration-apply-${m.id}`}
                      >
                        {busy === m.id && <CircleNotch size={11} className="animate-spin" />}
                        {m.target_exists
                          ? tr("Déplacer vers ce terme", "Move to that term")
                          : tr("Créer le terme et déplacer", "Create the term and move")}
                      </button>
                      <button
                        type="button"
                        onClick={() => agir(m, "dismiss")}
                        disabled={busy === m.id}
                        className="text-[11px] px-3 py-1.5 rounded border border-zinc-300 text-zinc-600 hover:bg-white disabled:opacity-50"
                        data-testid={`migration-dismiss-${m.id}`}
                      >
                        {tr("Écarter", "Dismiss")}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Tâche 1452 (Eric 2026-06) — Les TROIS issues, en pied de fenêtre.
            ⚠️ « Refuser » n'est pas « ne rien faire » : la définition réécrite
            est DÉJÀ en base (choix de 1353), donc refuser REMET le texte
            d'avant, pris dans l'historique. La fusion des termes, elle, n'est
            jamais défaite : Eric refuse le texte, pas le regroupement qu'il
            vient de demander. */}
        <div
          className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex flex-wrap items-center gap-2 justify-end"
          data-testid="reformulation-decisions"
        >
          <span className="text-[11px] text-zinc-500 mr-auto">
            {migrations.length > 0
              ? tr(`${migrations.length} nouveau(x) terme(s) proposé(s).`,
                   `${migrations.length} new term(s) proposed.`)
              : tr("Aucun nouveau terme proposé.", "No new term proposed.")}
          </span>
          <button
            type="button"
            onClick={() => decider("refuser")}
            disabled={!!busy}
            className="text-[12px] font-semibold px-3 py-2 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="reformulation-refuser"
            title={tr("Remet la définition telle qu'elle était avant la reformulation, et écarte les nouveaux termes. La fusion des termes reste faite.",
                      "Restores the definition as it was before the reformulation and dismisses the new terms. The merge itself stays.")}
          >
            {busy === "refuser" && <CircleNotch size={12} className="animate-spin" />}
            {tr("Refuser", "Refuse")}
          </button>
          <button
            type="button"
            onClick={() => decider("reduire")}
            disabled={!!busy}
            className="text-[12px] font-semibold px-3 py-2 rounded border border-zinc-300 text-zinc-700 hover:bg-white disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="reformulation-reduire"
            title={tr("Garde la définition raccourcie, et n'ouvre aucun nouveau terme.",
                      "Keeps the shortened definition and opens no new term.")}
          >
            {busy === "reduire" && <CircleNotch size={12} className="animate-spin" />}
            {tr("Réduire seulement", "Shorten only")}
          </button>
          <button
            type="button"
            onClick={() => decider("reduire_distribuer")}
            disabled={!!busy || migrations.length === 0}
            className="text-[12px] font-semibold px-3 py-2 rounded bg-[#002FA7] text-white hover:bg-[#001F7A] disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="reformulation-reduire-distribuer"
            title={migrations.length === 0
              ? tr("Aucun fragment à déplacer : l'IA n'a proposé aucun autre terme.",
                   "No fragment to move: the AI proposed no other term.")
              : tr("Garde la définition raccourcie ET déplace chaque fragment vers son terme, en le créant au besoin.",
                   "Keeps the shortened definition AND moves each fragment to its term, creating it if needed.")}
          >
            {busy === "reduire_distribuer" && <CircleNotch size={12} className="animate-spin" />}
            {tr("Réduire + distribuer", "Shorten + distribute")}
          </button>
        </div>
      </div>
    </div>
  );
}
